import React, { useEffect, useState, useCallback } from "react";
import { Link as LinkRouterDom, RouteComponentProps } from "react-router-dom";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { string, object, boolean } from "yup";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Link from "@material-ui/core/Link";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import FormInput from "components/FormInput";
import useStyles from "../styles";
import { routes } from "ui/routes";
import { useAuth } from "data/auth/hooks";
import { useSnackbar } from "data/snackbar/hooks";
import { CircularProgress } from "@material-ui/core";
import PasswordTooltip from "./PasswordTooltip";

const getPasswordValidation = (password:string) =>{
  const length = password.length >= 8;
  const lower = /[a-z]/.test(password);
  const upper = /[A-Z]/.test(password);
  const number = /[0-9]/.test(password);
  const special = /[!@#$%^&*]/.test(password);
  const passedTypes = [lower, upper, number, special].filter(Boolean).length;
  return {
    length: length,
    lower: lower,
    upper: upper,
    number: number,
    special: special,
    passedTypes: passedTypes,
    valid: length && passedTypes >= 3,
  };
}

type CompleteInviteProps = RouteComponentProps<{ verificationToken: string }> & {};

const validationSchema = (t: TFunction) =>
  object({
    password: string().required(t("common.error.form.required"))
    .test(
        "password-strength-test",
        t("common.error.form.required"),
        (value) => !!value && getPasswordValidation(value).valid
      ),
    firstName: string().required(t("common.error.form.required")),
    lastName: string().required(t("common.error.form.required")),
    phoneNumber: string().required(t("common.error.form.required")),
    acceptTerms: boolean().required(t("common.error.form.required")),
  });

const CompleteInvite: React.FC<CompleteInviteProps> = ({ history, match: { params } }) => {
  const [t] = useTranslation();
  const styles = useStyles();
  const schema = React.useMemo(() => validationSchema(t), [t]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [, { pushMessage }] = useSnackbar();
  const [, { logout }] = useAuth();

  const [, { verifyUserRegistration, completeInvite }] = useAuth();

  const formik = useFormik({
    initialValues: {
      password: "",
      firstName: "",
      lastName: "",
      phoneNumber: "",
      acceptTerms: false,
    },
    validationSchema: schema,
    onSubmit: (values) => {
      completeInvite({
        registrationId: params.verificationToken,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        email: email,
        password: values.password,
      })
        .then(({ data: isSuccessful }) => {
          isSuccessful && history.push(routes.login.path);
        })
        .catch(() => pushMessage(t("login.completeInvite.unableToRegister")));
    },
  });

  useEffect(() => {
    async function AsyncUserRegistration() {
      if (params.verificationToken) {
        try {
          setLoading(true);
          const email = await verifyUserRegistration(params.verificationToken);
          setEmail(email);
          setLoading(false);
        } catch (e) {
          const message = t("login.completeInvite.verificationTokenExpired");
          sessionStorage.setItem("snackbarMessage", message);
          logout();
        }
      } else {
        setLoading(false); // Stop loading if no token
      }
    }
    AsyncUserRegistration();
  }, [verifyUserRegistration, params.verificationToken, history]);

  const submitForm = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      formik.handleSubmit();
    },
    [formik]
  );

  const passwordRules = getPasswordValidation(formik.values.password);

  if (loading) {
    // Show spinner while loading
    return (
      <Grid container style={{ justifyContent: "center", alignItems: "center" }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container direction="column" spacing={4}>
      <Grid item>
        <Typography variant="h5">{t("login.completeInvite.title")}</Typography>
      </Grid>
      <Grid item>
        <form>
          <Grid container direction="column" spacing={2}>
            <Grid item className={styles.inputContainer}>
              <FormInput
                required
                name="firstName"
                label={t("login.completeInvite.firstName")}
                value={formik.values.firstName}
                error={formik.errors.firstName}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item className={styles.inputContainer}>
              <FormInput
                required
                name="lastName"
                label={t("login.completeInvite.lastName")}
                value={formik.values.lastName}
                error={formik.errors.lastName}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item className={styles.inputContainer}>
              <FormInput required name="email" label={t("common.input.label.email")} value={email} />
            </Grid>
            <Grid item className={styles.inputContainer}>
              <FormInput
                required
                name="phoneNumber"
                error={formik.errors.phoneNumber}
                label={t("login.completeInvite.phone")}
                value={formik.values.phoneNumber}
                onChange={formik.handleChange}
              />
            </Grid>
            <Grid item className={styles.inputContainer}>
              <PasswordTooltip
                open={Boolean(formik.values.password)}
                passwordRules={passwordRules}
              >
                <FormInput
                  required
                  type="password"
                  name="password"
                  label={t("common.input.label.password")}
                  value={formik.values.password}
                  error={formik.errors.password}
                  onChange={formik.handleChange}
                />
              </PasswordTooltip>
            </Grid>
            <Grid item>
              <FormControlLabel
                name="acceptTerms"
                control={<Checkbox />}
                onChange={() => formik.setFieldValue("acceptTerms", !formik.values.acceptTerms)}
                label={
                  <div className={styles.termsContainer}>
                    <Typography component="span">{t("login.completeInvite.terms")}</Typography>
                    <Link
                      data-testid="terms-of-service-test-id"
                      className={styles.link}
                      href="https://www.us.endress.com/en/Terms-of-use"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography color="primary" component="span">
                        {t("login.completeInvite.termsOfService")}
                      </Typography>
                    </Link>
                    {/* <LinkRouterDom className={styles.link} to={routes.terms.path}>
                      <Typography color="primary" component="span">
                        {t("login.completeInvite.termsOfService")}
                      </Typography>
                    </LinkRouterDom> */}
                    <Typography component="span">{t("login.completeInvite.and")}</Typography>
                    <Link
                      data-testid="privacy-policy-test-id"
                      className={styles.link}
                      href="https://www.us.endress.com/en/endress-hauser-privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography color="primary" component="span">
                        {t("login.completeInvite.privacyPolicy")}
                      </Typography>
                    </Link>
                  </div>
                }
              />
            </Grid>
            <Grid item>
              <Button type="submit" variant="contained" color="primary" onClick={submitForm}>
                {t("login.completeInvite.signUpBtn")}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

export default CompleteInvite;
